import { ApplicationState, IoState, isDone } from "#store/types";
import { isPresent, Optional } from "#types";
import {
    Interpretation,
    OperatorInvocation,
} from "#services/pilotService/types";
import { IQueryTableResult } from "#services/pilotService/responses/types";
import {
    selectId,
    selectIds,
    selectInterpretationById,
    selectTableByInterpretationId,
} from "#selectors";
import { createSelector } from "reselect";
import { OperatorInvocationMap } from "#store/operatorInvocations";

/**
 * @deprecated doesnt bring the benefit that i hoped for. Better to use just numeric ids and resolve them as late as possible.
 */
export type OperatorView = {
    operator: OperatorInvocation;
    interpretations: Interpretation[];
    tables: IQueryTableResult[];
};

/**
 * @deprecated
 * @param state
 */
export const selectOperatorById = (
    state: ApplicationState,
    id: number
): Optional<OperatorView> => {
    const operatorInvocation: Optional<IoState<OperatorInvocation>> =
        state.operatorInvocations.operatorInvocations[id];

    if (!operatorInvocation) {
        return null;
    }
    if (operatorInvocation.state !== "done") {
        return null;
    }

    const interpretations: Interpretation[] =
        operatorInvocation.payload.interpretations
            .map(iid => selectInterpretationById(state, iid))
            .filter(isPresent);

    const tables: IQueryTableResult[] = interpretations
        .map(i => selectTableByInterpretationId(state, i.id))
        .filter(isPresent);

    return {
        operator: operatorInvocation.payload,
        interpretations,
        tables,
    };
};

export const selectAllOperatorInvocations = (
    state: ApplicationState
): OperatorView[] => {
    return Object.keys(state.operatorInvocations.operatorInvocations)
        .map(k => selectOperatorById(state, Number(k)))
        .filter(isPresent);
};

export const selectOperatorInvocationsMap = (
    state: ApplicationState
): OperatorInvocationMap => {
    return state.operatorInvocations.operatorInvocations;
};

export const selectOperatorInvocationsIoState = createSelector(
    [selectOperatorInvocationsMap],
    (map): IoState<OperatorInvocation>[] => {
        return Object.values(map);
    }
);

const selectOperatorThatAreDoneAndPresent = createSelector(
    [selectOperatorInvocationsIoState],
    (ops: IoState<OperatorInvocation>[]): OperatorInvocation[] => {
        return ops
            .filter(isDone)
            .map(x => x.payload)
            .filter(isPresent);
    }
);

export const selectOperatorInvocationRawById = createSelector(
    [selectOperatorInvocationsMap, selectId],
    (map, id): Optional<IoState<OperatorInvocation>> => {
        return map[id];
    }
);

export const selectOperatorInvocationsRawByIds = createSelector(
    [selectOperatorInvocationsMap, selectIds],
    (map, ids): Optional<IoState<OperatorInvocation>>[] => {
        return ids.map(id => map[id]);
    }
);

export const selectOperatorInvocationsByIds = createSelector(
    [selectOperatorInvocationsRawByIds],
    (list): OperatorInvocation[] => {
        return list
            .filter(isPresent)
            .filter(isDone)
            .map(x => x.payload)
            .filter(isPresent);
    }
);

export const selectAllOperatorInvocationsByActionId = createSelector(
    [selectOperatorThatAreDoneAndPresent, selectId],
    (ops, id): OperatorInvocation[] => {
        return ops.filter(x => x.action === id);
    }
);
